import * as React from 'react';
import {ErrorResponse, IncomingInvoiceItem, Item} from '../api/dtos/dtos';
import {
  ApplicationContextConsumer,
  bindLoadingHandler,
  LoadingOverlayHandler,
  withLoadingOverlay
} from '../app/ApplicationContext';
import {ErrorAlert} from '../common/ErrorAlert';
import {ColumnProps} from 'antd/lib/table';
import * as tableUtils from '../utils/table-utils';
import {filterableColumn, GridState, searchableSortableColumn, sortableColumn,} from '../utils/table-utils';
import {
  getIncomingInvoiceStateFilters,
  IncomingInvoiceState,
  renderIncomingInvoiceStateDot
} from './incoming-invoice-state';
import {alignRightNoWrap, groupNumber} from '../utils/number-utils';
import {formatDate, formatDateOrNull} from '../utils/date-utils';
import furoApi from "../api/FuroApi";
import locales from '../locales';
import {Button, Table, Tag} from 'antd';
import {Link} from 'react-router-dom';
import * as paths from '../paths';
import {showConfirm} from '../utils/dialogs';
import ButtonGroup from 'antd/lib/button/button-group';

const STATE_KEY: string = 'state';

const initialGridState = (): GridState<IncomingInvoiceItem> => {
  return {
    ...tableUtils.initialGridState(10),
    sortColumnKey: 'issueDate',
    sortOrder: 'descend',
    filters: {
      [STATE_KEY]: [IncomingInvoiceState.OPEN, IncomingInvoiceState.TO_BE_TRANSFERRED, IncomingInvoiceState.TRANSFERRED],
    },
  }
};

interface Props {
  item: Item;
  reload: boolean;
}

interface State extends GridState<IncomingInvoiceItem> {
  error?: ErrorResponse;
}

class ItemInvoicesPage extends React.Component<Props, State> {

  public readonly loadingHandler?: LoadingOverlayHandler;
  public state: State = {
    ...initialGridState(),
  };

  public componentDidMount(): void {
    this.reload();
  }

  public componentDidUpdate(prevProps: Readonly<Props>, prevState: Readonly<State>) {
    if (this.props.reload && prevProps.reload !== this.props.reload) {
      this.reload();
    }
  }

  public render() {
    const {data, error} = this.state;
    const columns: Array<ColumnProps<IncomingInvoiceItem>> = [
      {
        width: 38,
        ...filterableColumn('', STATE_KEY, this.state, r => renderIncomingInvoiceStateDot(r.state), getIncomingInvoiceStateFilters(), true),
        className: 'align-center'
      },
      {
        width: 180,
        ...searchableSortableColumn(locales.appellation, 'name', this.state, r => this.renderInvoiceName(r)),
      },
      searchableSortableColumn(locales.type, 'type', this.state, r => r.type),
      searchableSortableColumn(locales.company_name, 'companyName', this.state, r => r.companyName),
      searchableSortableColumn(locales.invoice_number, 'invoiceNumber', this.state, r => r.invoiceNumber),
      sortableColumn(locales.sum, 'sum', this.state, r => this.renderSumWithCurrency(r)),
      sortableColumn(locales.issue_date, 'issueDate', this.state, r => formatDate(r.issueDate)),
      sortableColumn(locales.paid, 'paidDate', this.state, r => formatDateOrNull(r.paidDate)),
      {key: 'actions', width: 80, render: this.renderActionColumn}
    ];
    return (
      <>
        <ApplicationContextConsumer>
          {({loadingHandler}) => bindLoadingHandler(loadingHandler, this)}
        </ApplicationContextConsumer>
        <Table
          dataSource={data}
          columns={columns}
          bordered={true}
          size="small"
          scroll={{x: 1024}}
          rowKey={tableUtils.idRowKeyFn}
          pagination={tableUtils.defaultPaginationConfig(this.state)}
          onChange={tableUtils.handleOnGridChange(this.state, this.onGridChange)}
        />
        <ErrorAlert style={{marginTop: 16}} error={error}/>
      </>
    );
  }

  private renderInvoiceName = (invoice: IncomingInvoiceItem) => {
    return (
      <div style={{display: 'inline-grid'}}>
        <Link to={paths.viewIncomingInvoicePath(invoice.id)}>{invoice.name}</Link>
        {invoice.handled && <Tag color="green">{locales.handled}</Tag>}
      </div>
    );
  };

  private renderSumWithCurrency = (invoice: IncomingInvoiceItem) => {
    const unit = invoice.currency === 'EUR' ? locales.currency_eur : locales.app_currency;
    return alignRightNoWrap(<>{groupNumber(invoice.sum)} {unit}</>);
  };

  private renderActionColumn = (text: any, invoice: IncomingInvoiceItem, index: number) => {
    const onHandleItemClicked = () => {
      const title = invoice.handled ? locales.handle_item_revert : locales.handle_item_confirm;
      showConfirm(title, () => this.handleItemOnInvoice(invoice));
    };
    const onDeleteItemClicked = () => {
      showConfirm(locales.incoming_invoice_item_detach_confirm, () => this.removeItemFromInvoice(invoice));
    };
    return (
      <div className="action-column" style={{textAlign: 'right'}}>
        <ButtonGroup>
          <Button
            icon={invoice.handled ? "close-circle" : "check-circle"}
            title={invoice.handled ? locales.revert : locales.handle}
            style={{width: 32, padding: 0}}
            onClick={onHandleItemClicked}
          />
          <Button
            icon="delete"
            onClick={onDeleteItemClicked}
            title={locales.detach}
          />
        </ButtonGroup>
      </div>
    );
  };

  private onGridChange = (newState: GridState<IncomingInvoiceItem>) => {
    this.setState({...newState}, () => this.reload());
  };

  private reload = () => {
    const promise = this.loadItemInvoices()
      .catch(this.onApiError);
    withLoadingOverlay(promise, this.loadingHandler);
  };

  private handleItemOnInvoice = (invoice: IncomingInvoiceItem) => {
    const {item} = this.props;
    const handled = !invoice.handled;
    const promise = furoApi.incomingInvoices.handleItem(invoice.id!!, item.id!!, handled)
      .then((_) => this.loadItemInvoices())
      .catch(this.onApiError);
    withLoadingOverlay(promise, this.loadingHandler);
  };

  private removeItemFromInvoice = (invoice: IncomingInvoiceItem) => {
    const {item} = this.props;
    const promise = furoApi.incomingInvoices.removeItem(invoice.id!!, item.id!!)
      .then((_) => this.loadItemInvoices())
      .catch(this.onApiError);
    withLoadingOverlay(promise, this.loadingHandler);
  };

  private loadItemInvoices = (): Promise<void> => {
    const {item} = this.props;
    const request = tableUtils.toGridRequest(this.state);
    return furoApi.incomingInvoices.getAllByItem(item.id!!, request)
      .then((response) => this.setState({
        ...tableUtils.toGridState(response),
        error: undefined,
      }));
  };

  private onApiError = (error: ErrorResponse) => this.setState({error});
}

export default ItemInvoicesPage;


// WEBPACK FOOTER //
// ./src/incoming-invoices/ItemInvoicesPage.tsx